import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTenantStore } from '@/hooks/useTenantStore';
import { Loader2, Trash2, AlertTriangle } from 'lucide-react';
import * as CapLedgerService from '@/services/capLedgerService';
import { ShareCapitalHistory } from './ShareCapitalHistory';
import { ShareCapitalForecast } from './ShareCapitalForecast';

export function CompanySettings() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { tenantId, setTenantId } = useTenantStore();
  const [loading, setLoading] = useState(true);
  const [watermarkEnabled, setWatermarkEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [authorizedShares, setAuthorizedShares] = useState<string>('');
  const [savingShares, setSavingShares] = useState(false);
  const [authorizedSharesNum, setAuthorizedSharesNum] = useState<number | null>(null);

  useEffect(() => {
    if (tenantId) {
      loadSettings();
    }
  }, [tenantId]);

  const loadSettings = async () => {
    if (!tenantId) return;

    setLoading(true);
    try {
      const { data: company, error } = await supabase
        .from('companies')
        .select('name, watermark_enabled, authorized_shares')
        .eq('id', tenantId)
        .single();

      if (error) throw error;

      if (company) {
        setWatermarkEnabled(company.watermark_enabled ?? true);
        setCompanyName(company.name);
        const authShares = company.authorized_shares;
        setAuthorizedShares(authShares?.toString() || '');
        setAuthorizedSharesNum(authShares || null);
      }
    } catch (error) {
      console.error('Error loading company settings:', error);
      toast({
        title: 'Error',
        description: 'Failed to load company settings',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleWatermarkToggle = async (enabled: boolean) => {
    if (!tenantId) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('companies')
        .update({ watermark_enabled: enabled })
        .eq('id', tenantId);

      if (error) throw error;

      setWatermarkEnabled(enabled);
      toast({
        title: 'Settings updated',
        description: `Document watermarks ${enabled ? 'enabled' : 'disabled'}`,
      });
    } catch (error) {
      console.error('Error updating watermark setting:', error);
      toast({
        title: 'Error',
        description: 'Failed to update watermark setting',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!tenantId || deleteConfirmation !== companyName) return;

    setDeleting(true);
    try {
      // Delete the company - cascading deletes will handle related data
      const { error } = await supabase.from('companies').delete().eq('id', tenantId);

      if (error) throw error;

      toast({
        title: 'Company deleted',
        description: `${companyName} has been permanently deleted`,
      });

      // Clear tenant and navigate to companies page
      setTenantId('');
      setDeleteDialogOpen(false);
      navigate('/companies');
    } catch (error: any) {
      console.error('Error deleting company:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete company',
        variant: 'destructive',
      });
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="text-muted-foreground">Please select a company to view settings.</p>
        </CardContent>
      </Card>
    );
  }

  const handleAuthorizedSharesUpdate = async () => {
    if (!tenantId) return;

    setSavingShares(true);
    try {
      const shares = authorizedShares.trim() === '' ? null : parseInt(authorizedShares);

      if (shares !== null && (isNaN(shares) || shares < 0)) {
        toast({
          title: 'Invalid input',
          description: 'Please enter a valid number of shares',
          variant: 'destructive',
        });
        return;
      }

      const { error } = await supabase
        .from('companies')
        .update({ authorized_shares: shares })
        .eq('id', tenantId);

      if (error) throw error;

      toast({
        title: 'Settings updated',
        description:
          shares === null
            ? 'Authorized shares cap removed'
            : `Authorized shares set to ${shares.toLocaleString()}`,
      });

      // Update local state for forecast
      setAuthorizedSharesNum(shares);
    } catch (error: any) {
      console.error('Error updating authorized shares:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update authorized shares',
        variant: 'destructive',
      });
    } finally {
      setSavingShares(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Share Capital</CardTitle>
          <CardDescription>Configure authorized share capital and cap table limits</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="authorized-shares">Authorized Shares</Label>
            <p className="text-sm text-muted-foreground">
              Total number of shares the company is authorized to issue. Leave empty for no limit.
            </p>
            <div className="flex gap-2">
              <Input
                id="authorized-shares"
                type="number"
                value={authorizedShares}
                onChange={(e) => setAuthorizedShares(e.target.value)}
                placeholder="e.g., 10000000"
                min="0"
                className="max-w-xs [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
              />
              <Button onClick={handleAuthorizedSharesUpdate} disabled={savingShares}>
                {savingShares ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Update'
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              ⚠️ Share issuances and adjustments will be validated against this cap
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Share Capital History */}
      {tenantId && <ShareCapitalHistory companyId={tenantId} />}

      {/* Share Capital Forecast */}
      {tenantId && (
        <ShareCapitalForecast companyId={tenantId} authorizedShares={authorizedSharesNum} />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Data Room Settings</CardTitle>
          <CardDescription>Configure how documents are shared in your data room</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="watermark">Document Watermarks</Label>
              <p className="text-sm text-muted-foreground">
                Display viewer email and timestamp on shared documents
              </p>
            </div>
            <Switch
              id="watermark"
              checked={watermarkEnabled}
              onCheckedChange={handleWatermarkToggle}
              disabled={saving}
            />
          </div>
        </CardContent>
      </Card>

      {/* Share Capital History */}
      {tenantId && <ShareCapitalHistory companyId={tenantId} />}

      {/* Danger Zone */}
      <Card className="border-destructive/50">
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
          </div>
          <CardDescription>
            Irreversible actions that permanently affect your company data
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-4 border border-destructive/30 rounded-lg bg-destructive/5">
            <div className="space-y-1">
              <Label className="text-base font-semibold">Delete Company</Label>
              <p className="text-sm text-muted-foreground">
                Permanently delete this company and all associated data including shareholders,
                ledger events, documents, and settings. This action cannot be undone.
              </p>
            </div>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => setDeleteDialogOpen(true)}
              className="shrink-0 ml-4"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete Company
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              Delete Company: {companyName}
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p className="font-semibold">
                This action cannot be undone. This will permanently delete:
              </p>
              <ul className="list-disc list-inside space-y-1 text-sm">
                <li>All shareholders and their holdings</li>
                <li>All ledger events and transaction history</li>
                <li>All funding rounds and financial data</li>
                <li>All documents and data room files</li>
                <li>All board resolutions and governance records</li>
                <li>All settings and configurations</li>
              </ul>
              <div className="pt-4">
                <Label htmlFor="confirm-delete" className="text-foreground">
                  Type <span className="font-mono font-bold">{companyName}</span> to confirm:
                </Label>
                <Input
                  id="confirm-delete"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  placeholder={companyName}
                  className="mt-2"
                  autoComplete="off"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                setDeleteConfirmation('');
                setDeleteDialogOpen(false);
              }}
              disabled={deleting}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCompany}
              disabled={deleteConfirmation !== companyName || deleting}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete Company
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
